import React, { useEffect } from "react";
import { ArrowRight, BarChart3, Globe2, Layers, TrendingUp, ShieldCheck, FileSearch, Compass, LineChart } from "lucide-react";
import { useI18n } from "@/lib/i18n";
import NewsletterForm from "@/components/NewsletterForm";

const WHY_ITEMS = [
  { key: "info", Icon: FileSearch },
  { key: "digital", Icon: Globe2 },
  { key: "projects", Icon: Layers },
  { key: "impact", Icon: TrendingUp },
];

const PROCESS_STEPS = [
  { key: "explore", Icon: Compass },
  { key: "define", Icon: Layers },
  { key: "follow", Icon: LineChart },
  { key: "analyze", Icon: BarChart3 },
];

const INDICATORS = ["lithium", "vacaMuerta", "gas", "mining"];

const Home = () => {
  const { t, localePath } = useI18n();

  useEffect(() => {
    document.title = t("pageTitles.home");
    const desc = document.querySelector('meta[name="description"]');
    if (desc) desc.setAttribute("content", t("meta.description"));
  }, [t]);

  return (
    <main data-testid="home-page" className="bg-[#F8FAFC] text-slate-900">
      {/* HERO */}
      <section className="bg-white border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-24 grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-6">
            <p className="text-xs font-semibold uppercase tracking-[0.22em] text-[#C85A32]">
              {t("hero.eyebrow")}
            </p>
            <h1 className="mt-4 font-heading text-4xl sm:text-5xl lg:text-6xl font-bold tracking-tight leading-[1.1] text-[#0B2545]">
              {t("hero.title")}
            </h1>
            <p className="mt-4 text-xl text-slate-700 font-semibold">
              {t("hero.subtitle")}
            </p>
            <p className="mt-5 text-base sm:text-lg text-slate-600 leading-relaxed">
              {t("hero.description")}
            </p>
            <p className="mt-4 text-base sm:text-lg text-slate-600 leading-relaxed">
              {t("hero.descriptionSecondary")}
            </p>

            <div className="mt-8 flex flex-col sm:flex-row gap-3">
              <a
                href="#subscribe"
                data-testid="hero-cta-primary"
                className="inline-flex items-center justify-center gap-2 bg-[#0B2545] text-white px-6 py-3 text-sm font-semibold tracking-wide hover:bg-[#133763]"
              >
                {t("hero.ctaPrimary")}
                <ArrowRight size={16} />
              </a>
              <a
                href="#process"
                data-testid="hero-cta-secondary"
                className="inline-flex items-center justify-center gap-2 border border-[#0B2545] text-[#0B2545] px-6 py-3 text-sm font-semibold tracking-wide hover:bg-[#0B2545] hover:text-white"
              >
                {t("hero.ctaSecondary")}
              </a>
            </div>
          </div>

          <div className="lg:col-span-6">
            <div className="relative">
              <img
                src="/images/lithium-salt-flats.jpg"
                alt={t("hero.imageAlt")}
                data-testid="hero-image"
                className="w-full h-[380px] sm:h-[460px] object-cover border border-slate-200"
                loading="eager"
              />
              <div className="absolute left-4 bottom-4 bg-white border border-slate-200 px-4 py-3 shadow-sm max-w-[240px]">
                <p className="text-[10px] uppercase tracking-widest text-slate-500">
                  {t("indicators.items.lithium.region")}
                </p>
                <p className="mt-1 font-heading text-2xl font-bold text-[#0B2545]">
                  {t("indicators.items.lithium.value")}
                </p>
                <p className="text-[11px] font-semibold text-[#15803D]">
                  {t("indicators.items.lithium.trend")}
                </p>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* INDICATORS */}
      <section id="sector" className="border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-20">
          <div className="flex flex-col lg:flex-row lg:items-end lg:justify-between gap-6">
            <div className="max-w-2xl">
              <p className="text-xs font-semibold uppercase tracking-[0.22em] text-[#C85A32]">
                {t("indicators.eyebrow")}
              </p>
              <h2 className="mt-3 font-heading text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-slate-900">
                {t("indicators.title")}
              </h2>
              <p className="mt-3 text-base text-slate-600 leading-relaxed">
                {t("indicators.subtitle")}
              </p>
            </div>
            <div className="text-xs text-slate-500 space-y-1 lg:text-right">
              <p className="font-semibold text-slate-700">{t("indicators.updated")}</p>
              <p>{t("indicators.source")}</p>
            </div>
          </div>

          <div className="mt-10 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-0 border border-slate-200 bg-white">
            {INDICATORS.map((key, idx) => (
              <div
                key={key}
                data-testid={`indicator-${key}`}
                className={
                  "p-6 sm:p-7 " +
                  (idx !== INDICATORS.length - 1
                    ? "border-b sm:border-b lg:border-b-0 sm:border-r-0 lg:border-r border-slate-200 "
                    : "") +
                  (idx % 2 === 0 ? "sm:border-r sm:border-slate-200 " : "") +
                  (idx < 2 ? "sm:border-b lg:border-b-0 sm:border-slate-200 " : "")
                }
              >
                <p className="text-[10px] uppercase tracking-widest text-slate-500">
                  {t(`indicators.items.${key}.region`)}
                </p>
                <p className="mt-3 font-heading text-3xl font-bold text-[#0B2545] tracking-tight">
                  {t(`indicators.items.${key}.value`)}
                </p>
                <p className="mt-1.5 text-[13px] font-semibold text-[#15803D]">
                  {t(`indicators.items.${key}.trend`)}
                </p>
                <p className="mt-3 text-sm text-slate-600 leading-snug">
                  {t(`indicators.items.${key}.label`)}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* WHY */}
      <section className="bg-white border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-24">
          <div className="max-w-3xl">
            <p className="text-xs font-semibold uppercase tracking-[0.22em] text-[#C85A32]">
              {t("why.eyebrow")}
            </p>
            <h2 className="mt-3 font-heading text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-slate-900">
              {t("why.title")}
            </h2>
            <p className="mt-3 text-base text-slate-600 leading-relaxed">
              {t("why.subtitle")}
            </p>
          </div>

          <div className="mt-10 grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6">
            {WHY_ITEMS.map(({ key, Icon }) => (
              <div
                key={key}
                data-testid={`why-item-${key}`}
                className="border border-slate-200 p-6 bg-[#F8FAFC] hover:bg-white hover:border-[#0B2545] transition-colors"
              >
                <span className="inline-flex items-center justify-center w-11 h-11 bg-[#0B2545] text-white">
                  <Icon size={20} strokeWidth={2} />
                </span>
                <h3 className="mt-5 font-heading text-lg font-semibold text-slate-900">
                  {t(`why.items.${key}.title`)}
                </h3>
                <p className="mt-2 text-sm text-slate-600 leading-relaxed">
                  {t(`why.items.${key}.description`)}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* PROCESS */}
      <section id="process" className="border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-24 grid grid-cols-1 lg:grid-cols-12 gap-12">
          <div className="lg:col-span-4">
            <p className="text-xs font-semibold uppercase tracking-[0.22em] text-[#C85A32]">
              {t("process.eyebrow")}
            </p>
            <h2 className="mt-3 font-heading text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-slate-900">
              {t("process.title")}
            </h2>
            <p className="mt-3 text-base text-slate-600 leading-relaxed">
              {t("process.subtitle")}
            </p>
            <img
              src="/images/mining-machinery.jpg"
              alt={t("transparency.imageAlt")}
              className="mt-8 hidden lg:block w-full h-64 object-cover border border-slate-200"
            />
          </div>

          <ol className="lg:col-span-8 space-y-0 border-l-2 border-[#C85A32]/25">
            {PROCESS_STEPS.map(({ key, Icon }) => (
              <li
                key={key}
                data-testid={`process-step-${key}`}
                className="relative pl-8 py-6 border-b border-slate-200 last:border-b-0"
              >
                <span className="absolute -left-[13px] top-7 w-6 h-6 flex items-center justify-center bg-[#C85A32] text-white">
                  <Icon size={12} />
                </span>
                <div className="flex flex-col sm:flex-row sm:items-baseline sm:gap-6">
                  <span className="font-mono text-xs font-semibold text-[#C85A32] tracking-widest">
                    {t(`process.steps.${key}.step`)}
                  </span>
                  <div>
                    <h3 className="font-heading text-lg sm:text-xl font-semibold text-slate-900">
                      {t(`process.steps.${key}.title`)}
                    </h3>
                    <p className="mt-2 text-sm sm:text-base text-slate-600 leading-relaxed">
                      {t(`process.steps.${key}.description`)}
                    </p>
                  </div>
                </div>
              </li>
            ))}
          </ol>
        </div>
      </section>

      {/* TRANSPARENCY */}
      <section id="transparency" className="bg-white border-b border-slate-200">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-24 grid grid-cols-1 lg:grid-cols-12 gap-12 items-center">
          <div className="lg:col-span-7 order-2 lg:order-1">
            <p className="text-xs font-semibold uppercase tracking-[0.22em] text-[#C85A32]">
              {t("transparency.eyebrow")}
            </p>
            <h2 className="mt-3 font-heading text-2xl sm:text-3xl lg:text-4xl font-bold tracking-tight text-slate-900">
              {t("transparency.title")}
            </h2>
            <p className="mt-5 text-base text-slate-600 leading-relaxed">
              {t("transparency.paragraphOne")}
            </p>
            <p className="mt-4 text-base text-slate-600 leading-relaxed">
              {t("transparency.paragraphTwo")}
            </p>

            <ul className="mt-6 space-y-3">
              {["audit", "regulation", "esg"].map((k) => (
                <li
                  key={k}
                  data-testid={`transparency-bullet-${k}`}
                  className="flex items-start gap-3 text-sm text-slate-700"
                >
                  <span className="mt-0.5 w-6 h-6 shrink-0 flex items-center justify-center bg-[#0B2545] text-white">
                    <ShieldCheck size={14} />
                  </span>
                  <span>{t(`transparency.bullets.${k}`)}</span>
                </li>
              ))}
            </ul>
          </div>
          <div className="lg:col-span-5 order-1 lg:order-2">
            <img
              src="/images/vaca-muerta.jpg"
              alt={t("transparency.imageAlt")}
              className="w-full h-[380px] object-cover border border-slate-200"
            />
          </div>
        </div>
      </section>

      {/* NEWSLETTER */}
      <section id="subscribe" className="bg-[#F8FAFC]">
        <div className="max-w-5xl mx-auto px-4 sm:px-6 lg:px-8 py-16 sm:py-24">
          <NewsletterForm />
        </div>
      </section>
    </main>
  );
};

export default Home;
